/**
 * Deck health analysis hook
 *
 * Computes deck building warnings and commander category tracking
 * for smart suggestions and deck optimization.
 */
import { useMemo } from 'react';

import { isCommanderFormat, isLimitedFormat } from '@/lib/deckUtils';
import type { DeckFormat } from '@/types/deck';

import type { EnrichedDeckCard } from '../types';

// ============================================================================
// Types
// ============================================================================

export type WarningType =
  | 'low-lands'
  | 'high-lands'
  | 'color-sources'
  | 'no-removal'
  | 'heavy-curve'
  | 'no-card-draw'
  | 'no-ramp'
  | 'low-mana-fixing'
  | 'limited-low-creatures'
  | 'limited-low-two-drops';

export type WarningSeverity = 'info' | 'warning' | 'critical';

export interface DeckWarning {
  /** Warning type identifier */
  type: WarningType;
  /** Severity level */
  severity: WarningSeverity;
  /** Short title for the warning */
  title: string;
  /** Detailed explanation */
  description: string;
  /** Actionable suggestion */
  suggestion: string;
  /** Relevant stat values for context */
  stats?: { current: number; recommended: number; label: string };
}

export interface CommanderCategory {
  /** Category identifier */
  id: 'ramp' | 'card-draw' | 'removal' | 'board-wipes' | 'protection' | 'recursion';
  /** Display name */
  name: string;
  /** Current count of cards in this category */
  count: number;
  /** Minimum recommended count */
  minRecommended: number;
  /** Maximum recommended count */
  maxRecommended: number;
  /** Status based on count vs recommendations */
  status: 'low' | 'good' | 'high';
  /** Suggestion if count is off */
  suggestion: string | null;
}

export interface DeckHealthAnalysis {
  /** List of warnings/suggestions for the deck */
  warnings: DeckWarning[];
  /** Commander-specific category tracking (null for non-commander formats) */
  commanderCategories: CommanderCategory[] | null;
  /** Overall health score 0-100 */
  healthScore: number;
}

// ============================================================================
// Constants
// ============================================================================

/** Recommended minimums by format */
const FORMAT_THRESHOLDS: Record<string, { minLands: number; maxLands: number; avgCmcHeavy: number }> = {
  standard: { minLands: 20, maxLands: 26, avgCmcHeavy: 3.5 },
  pioneer: { minLands: 20, maxLands: 26, avgCmcHeavy: 3.5 },
  modern: { minLands: 18, maxLands: 24, avgCmcHeavy: 3.2 },
  legacy: { minLands: 16, maxLands: 22, avgCmcHeavy: 2.8 },
  vintage: { minLands: 14, maxLands: 20, avgCmcHeavy: 2.5 },
  pauper: { minLands: 18, maxLands: 24, avgCmcHeavy: 3.2 },
  commander: { minLands: 32, maxLands: 40, avgCmcHeavy: 3.5 },
  brawl: { minLands: 22, maxLands: 28, avgCmcHeavy: 3.5 },
  oathbreaker: { minLands: 22, maxLands: 28, avgCmcHeavy: 3.5 },
  casual: { minLands: 20, maxLands: 26, avgCmcHeavy: 3.5 },
  draft: { minLands: 15, maxLands: 18, avgCmcHeavy: 3.2 },
  sealed: { minLands: 15, maxLands: 18, avgCmcHeavy: 3.2 },
  jumpstart: { minLands: 15, maxLands: 18, avgCmcHeavy: 3.2 },
};

const DEFAULT_THRESHOLDS = { minLands: 20, maxLands: 26, avgCmcHeavy: 3.5 };

/** Mana fixing thresholds by deck color count */
const MANA_FIXING_THRESHOLDS: Record<number, { min: number; recommended: number }> = {
  3: { min: 6, recommended: 10 }, // 3-color deck
  4: { min: 10, recommended: 15 }, // 4-color deck
  5: { min: 14, recommended: 20 }, // 5-color deck
};

/** Commander category recommendations (card counts) */
const COMMANDER_CATEGORY_CONFIG: Record<string, { min: number; max: number }> = {
  ramp: { min: 10, max: 15 },
  'card-draw': { min: 10, max: 15 },
  removal: { min: 8, max: 12 },
  'board-wipes': { min: 3, max: 5 },
  protection: { min: 3, max: 8 },
  recursion: { min: 3, max: 8 },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get category status based on count vs recommendations
 */
function getCategoryStatus(count: number, min: number, max: number): 'low' | 'good' | 'high' {
  if (count < min) return 'low';
  if (count > max) return 'high';
  return 'good';
}

/**
 * Generate suggestion for a category
 */
function getCategorySuggestion(id: string, count: number, min: number, max: number): string | null {
  if (count < min) {
    const needed = min - count;
    return `Add ${needed} more ${formatCategoryName(id)} card${needed !== 1 ? 's' : ''}`;
  }
  if (count > max) {
    const excess = count - max;
    return `Consider cutting ${excess} ${formatCategoryName(id)} card${excess !== 1 ? 's' : ''}`;
  }
  return null;
}

/**
 * Format category ID to display name
 */
function formatCategoryName(id: string): string {
  const names: Record<string, string> = {
    ramp: 'ramp',
    'card-draw': 'card draw',
    removal: 'removal',
    'board-wipes': 'board wipe',
    protection: 'protection',
    recursion: 'recursion',
  };
  return names[id] ?? id;
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Analyze deck health and generate warnings/suggestions.
 *
 * @param cards - Array of deck cards with card data
 * @param format - Deck format for format-specific recommendations
 * @param manaBaseAnalysis - Optional mana base data for color source warnings
 */
export default function useDeckHealth(
  cards: EnrichedDeckCard[],
  format: DeckFormat | null,
  manaBaseAnalysis?: { landRecommendation: { current: number; recommended: number; isWarning: boolean; difference: number } },
): DeckHealthAnalysis {
  return useMemo(() => {
    // Filter to main deck zones
    const mainZones = ['main', 'commander', 'companion'];
    const relevantCards = cards.filter((c) => mainZones.includes(c.zone));

    // Basic statistics
    let totalCards = 0;
    let landCount = 0;
    let cmcSum = 0;
    let nonLandCount = 0;

    // Limited format tracking
    let creatureCount = 0;
    let twoCmcCreatureCount = 0;

    // Commander category counts
    let rampCount = 0;
    let cardDrawCount = 0;
    let removalCount = 0;
    let boardWipeCount = 0;
    let protectionCount = 0;
    let recursionCount = 0;
    let manaFixingCount = 0;

    // Track deck color identity for mana fixing warning
    const deckColors = new Set<string>();

    // Analyze each card
    for (const deckCard of relevantCards) {
      const card = deckCard.card;
      const qty = deckCard.quantity;
      totalCards += qty;

      const cardIsLand = deckCard.classification.is_land;

      // Collect deck color identity
      if (card.color_identity) {
        for (const color of card.color_identity) {
          deckColors.add(color);
        }
      }

      if (cardIsLand) {
        landCount += qty;
      } else {
        nonLandCount += qty;
        cmcSum += (card.cmc ?? 0) * qty;

        // Track creatures for limited format analysis
        if (deckCard.classification.is_creature) {
          creatureCount += qty;
          if ((card.cmc ?? 0) === 2) {
            twoCmcCreatureCount += qty;
          }
        }
      }

      // Classify for commander categories (including mana fixing for both lands and non-lands)
      const roles = deckCard.classification.roles;

      if (roles.ramp) rampCount += qty;
      if (roles.cardDraw) cardDrawCount += qty;
      if (roles.removal) removalCount += qty;
      if (roles.boardWipe) boardWipeCount += qty;
      if (roles.protection) protectionCount += qty;
      if (roles.recursion) recursionCount += qty;
      if (roles.manaFixing) manaFixingCount += qty;
    }

    const avgCmc = nonLandCount > 0 ? cmcSum / nonLandCount : 0;
    const thresholds = format ? (FORMAT_THRESHOLDS[format] ?? DEFAULT_THRESHOLDS) : DEFAULT_THRESHOLDS;
    const deckColorCount = deckColors.size;

    // ========================================================================
    // Generate Warnings
    // ========================================================================

    const warnings: DeckWarning[] = [];

    // Only generate warnings if we have a significant number of cards
    if (totalCards >= 30) {
      // Land warnings (use mana base analysis if available, otherwise basic check)
      if (manaBaseAnalysis?.landRecommendation) {
        const { current, recommended, isWarning, difference } = manaBaseAnalysis.landRecommendation;
        if (isWarning && difference < -2) {
          warnings.push({
            type: 'low-lands',
            severity: difference < -4 ? 'critical' : 'warning',
            title: 'Low land count',
            description: `Your deck has ${current} lands but ${recommended} are recommended for this format and curve.`,
            suggestion: `Add ${-difference} more lands to avoid missing land drops consistently.`,
            stats: { current, recommended, label: 'lands' },
          });
        } else if (isWarning && difference > 3) {
          warnings.push({
            type: 'high-lands',
            severity: 'info',
            title: 'High land count',
            description: `Your deck has ${current} lands, which is ${difference} more than recommended.`,
            suggestion: 'Consider cutting lands for more spells—unless you have consistent land synergies.',
            stats: { current, recommended, label: 'lands' },
          });
        }
      } else {
        // Fallback basic land check
        if (landCount < thresholds.minLands - 2) {
          warnings.push({
            type: 'low-lands',
            severity: landCount < thresholds.minLands - 4 ? 'critical' : 'warning',
            title: 'Low land count',
            description: `Your deck has only ${landCount} lands. Most ${format ?? 'constructed'} decks need ${thresholds.minLands}-${thresholds.maxLands} lands.`,
            suggestion: `Add ${thresholds.minLands - landCount} more lands to improve consistency.`,
            stats: { current: landCount, recommended: thresholds.minLands, label: 'lands' },
          });
        }
      }

      // Heavy curve warning
      if (avgCmc > thresholds.avgCmcHeavy && nonLandCount > 20) {
        warnings.push({
          type: 'heavy-curve',
          severity: avgCmc > thresholds.avgCmcHeavy + 0.5 ? 'warning' : 'info',
          title: 'Heavy mana curve',
          description: `Average mana value is ${avgCmc.toFixed(2)}, which is higher than typical for ${format ?? 'constructed'} (usually under ${thresholds.avgCmcHeavy}).`,
          suggestion: 'Add more low-cost cards or ramp to support the curve, or cut expensive cards.',
          stats: { current: Math.round(avgCmc * 10) / 10, recommended: thresholds.avgCmcHeavy, label: 'avg CMC' },
        });
      }

      // Removal warning (for non-commander, simpler check)
      if (!isCommanderFormat(format) && removalCount < 4 && totalCards >= 50) {
        warnings.push({
          type: 'no-removal',
          severity: removalCount < 2 ? 'warning' : 'info',
          title: 'Limited removal',
          description: `Only ${removalCount} removal spells detected. Most decks benefit from interaction.`,
          suggestion: 'Add spot removal or counterspells to deal with threatening permanents.',
          stats: { current: removalCount, recommended: 6, label: 'removal spells' },
        });
      }

      // Card draw warning for commander
      if (isCommanderFormat(format) && cardDrawCount < 6) {
        warnings.push({
          type: 'no-card-draw',
          severity: cardDrawCount < 3 ? 'warning' : 'info',
          title: 'Low card draw',
          description: `Only ${cardDrawCount} card draw effects. Commander games are long—card advantage matters.`,
          suggestion: 'Add card draw engines to maintain resources throughout the game.',
          stats: { current: cardDrawCount, recommended: 10, label: 'draw effects' },
        });
      }

      // Ramp warning for commander
      if (isCommanderFormat(format) && rampCount < 6) {
        warnings.push({
          type: 'no-ramp',
          severity: rampCount < 4 ? 'warning' : 'info',
          title: 'Low ramp',
          description: `Only ${rampCount} ramp effects. Acceleration is key in multiplayer formats.`,
          suggestion: 'Add mana dorks, rocks, or land search to accelerate your gameplan.',
          stats: { current: rampCount, recommended: 10, label: 'ramp effects' },
        });
      }

      // Mana fixing warning for 3+ color decks
      if (deckColorCount >= 3) {
        const fixingThreshold = MANA_FIXING_THRESHOLDS[deckColorCount] ?? MANA_FIXING_THRESHOLDS[5];
        if (manaFixingCount < fixingThreshold.recommended) {
          const isCritical = manaFixingCount < fixingThreshold.min;
          warnings.push({
            type: 'low-mana-fixing',
            severity: isCritical ? 'critical' : 'warning',
            title: 'Low mana fixing',
            description: `Your ${deckColorCount}-color deck has only ${manaFixingCount} mana fixing sources. Multicolor decks need reliable color fixing to cast spells on curve.`,
            suggestion: 'Add dual lands, fetch lands, or mana fixing artifacts to improve color consistency.',
            stats: { current: manaFixingCount, recommended: fixingThreshold.recommended, label: 'fixing sources' },
          });
        }
      }

      // Limited format warnings
      if (isLimitedFormat(format)) {
        // Low creatures warning (limited needs 14-17 creatures typically)
        if (creatureCount < 13) {
          warnings.push({
            type: 'limited-low-creatures',
            severity: creatureCount < 10 ? 'warning' : 'info',
            title: 'Low creature count',
            description: `Your limited deck has ${creatureCount} creatures. Most limited decks want 14-17 creatures for consistent board presence.`,
            suggestion: 'Add more creatures to improve your board development.',
            stats: { current: creatureCount, recommended: 15, label: 'creatures' },
          });
        }

        // Low 2-drops warning (critical in limited for tempo)
        if (twoCmcCreatureCount < 4) {
          warnings.push({
            type: 'limited-low-two-drops',
            severity: twoCmcCreatureCount < 2 ? 'warning' : 'info',
            title: 'Low 2-drop creatures',
            description: `Only ${twoCmcCreatureCount} two-mana creatures. In limited, 2-drops are critical for tempo and early board presence.`,
            suggestion: 'Add more 2-drop creatures to improve your early game consistency.',
            stats: { current: twoCmcCreatureCount, recommended: 5, label: '2-drops' },
          });
        }
      }
    }

    // ========================================================================
    // Commander Categories
    // ========================================================================

    let commanderCategories: CommanderCategory[] | null = null;

    if (isCommanderFormat(format) && totalCards >= 40) {
      const categories: CommanderCategory[] = [
        {
          id: 'ramp',
          name: 'Ramp',
          count: rampCount,
          minRecommended: COMMANDER_CATEGORY_CONFIG.ramp.min,
          maxRecommended: COMMANDER_CATEGORY_CONFIG.ramp.max,
          status: getCategoryStatus(rampCount, COMMANDER_CATEGORY_CONFIG.ramp.min, COMMANDER_CATEGORY_CONFIG.ramp.max),
          suggestion: getCategorySuggestion('ramp', rampCount, COMMANDER_CATEGORY_CONFIG.ramp.min, COMMANDER_CATEGORY_CONFIG.ramp.max),
        },
        {
          id: 'card-draw',
          name: 'Card Draw',
          count: cardDrawCount,
          minRecommended: COMMANDER_CATEGORY_CONFIG['card-draw'].min,
          maxRecommended: COMMANDER_CATEGORY_CONFIG['card-draw'].max,
          status: getCategoryStatus(cardDrawCount, COMMANDER_CATEGORY_CONFIG['card-draw'].min, COMMANDER_CATEGORY_CONFIG['card-draw'].max),
          suggestion: getCategorySuggestion(
            'card-draw',
            cardDrawCount,
            COMMANDER_CATEGORY_CONFIG['card-draw'].min,
            COMMANDER_CATEGORY_CONFIG['card-draw'].max,
          ),
        },
        {
          id: 'removal',
          name: 'Removal',
          count: removalCount,
          minRecommended: COMMANDER_CATEGORY_CONFIG.removal.min,
          maxRecommended: COMMANDER_CATEGORY_CONFIG.removal.max,
          status: getCategoryStatus(removalCount, COMMANDER_CATEGORY_CONFIG.removal.min, COMMANDER_CATEGORY_CONFIG.removal.max),
          suggestion: getCategorySuggestion(
            'removal',
            removalCount,
            COMMANDER_CATEGORY_CONFIG.removal.min,
            COMMANDER_CATEGORY_CONFIG.removal.max,
          ),
        },
        {
          id: 'board-wipes',
          name: 'Board Wipes',
          count: boardWipeCount,
          minRecommended: COMMANDER_CATEGORY_CONFIG['board-wipes'].min,
          maxRecommended: COMMANDER_CATEGORY_CONFIG['board-wipes'].max,
          status: getCategoryStatus(
            boardWipeCount,
            COMMANDER_CATEGORY_CONFIG['board-wipes'].min,
            COMMANDER_CATEGORY_CONFIG['board-wipes'].max,
          ),
          suggestion: getCategorySuggestion(
            'board-wipes',
            boardWipeCount,
            COMMANDER_CATEGORY_CONFIG['board-wipes'].min,
            COMMANDER_CATEGORY_CONFIG['board-wipes'].max,
          ),
        },
      ];

      commanderCategories = categories;
    }

    // ========================================================================
    // Health Score
    // ========================================================================

    let healthScore = 100;

    // Deduct for warnings
    for (const warning of warnings) {
      switch (warning.severity) {
        case 'critical':
          healthScore -= 25;
          break;
        case 'warning':
          healthScore -= 15;
          break;
        case 'info':
          healthScore -= 5;
          break;
      }
    }

    // Deduct for commander categories below minimum
    if (commanderCategories) {
      for (const cat of commanderCategories) {
        if (cat.status === 'low') {
          healthScore -= 10;
        }
      }
    }

    healthScore = Math.max(0, healthScore);

    return {
      warnings,
      commanderCategories,
      healthScore,
    };
  }, [cards, format, manaBaseAnalysis]);
}
